import { formatarAteDuasCasas } from '@/lib/formato';

/**
 * A ULTIMA LEITURA DA MAQUINA, AO LADO DO CAMPO DO MEDIDOR (decisao 54).
 *
 * Pedido do dono (28/10/2026): "trazer no lancamento um campo similar a uma
 * tag mostrando qual e o ultimo horimetro ou km registrado para que o usuario
 * nao lance errado, principalmente no caso dos lancamentos offline". A
 * etiqueta fica ao lado do campo onde se digita o horimetro ou o km, no
 * celular e na mesa.
 *
 * SEM SINAL, A ULTIMA LEITURA E A DO APARELHO. O servidor sabe a leitura do
 * ultimo lancamento que CHEGOU; o abastecimento que ainda esta na fila deste
 * aparelho tambem leu o painel, e o proximo lancamento da mesma maquina tem
 * de partir dele. Sem isto, quem lanca duas vezes a mesma maquina sem rede via
 * a leitura de ontem e podia digitar um numero menor que o que ja lancou — e a
 * recusa so chegaria com a rede. O recusado nao conta: ele nao vai entrar.
 */

export interface UltimaLeitura {
  readonly valor: number;
  readonly em: Date | null;
  /** `true` quando a leitura vem de um lancamento que ainda esta na fila deste aparelho. */
  readonly doAparelho: boolean;
}

interface ItemDaFila {
  readonly rota: string;
  readonly corpo: Readonly<Record<string, unknown>>;
  readonly estado: string;
}

/** A maior leitura entre a do servidor e a dos abastecimentos desta maquina ainda na fila. Pura. */
export function ultimaLeituraComAFila(
  maquina: {
    readonly id: string;
    readonly ultimaLeitura: number | null;
    readonly ultimaLeituraEm: Date | null;
    /** Posto por `maquinaComAFila`: a ultima leitura ja e a de um lancamento deste aparelho. */
    readonly ultimaLeituraDoAparelho?: boolean;
  } | null,
  fila: readonly ItemDaFila[] = [],
): UltimaLeitura | null {
  if (!maquina) return null;
  let melhor: UltimaLeitura | null =
    maquina.ultimaLeitura === null
      ? null
      : { valor: maquina.ultimaLeitura, em: maquina.ultimaLeituraEm, doAparelho: maquina.ultimaLeituraDoAparelho === true };
  for (const item of fila) {
    if (item.rota !== '/combustivel/abastecimentos' || item.estado === 'recusado') continue;
    if (item.corpo['equipamentoId'] !== maquina.id) continue;
    const valor = Number(item.corpo['medidor']);
    if (item.corpo['medidor'] === null || item.corpo['medidor'] === undefined || !Number.isFinite(valor)) continue;
    if (melhor !== null && valor <= melhor.valor) continue;
    const quando = typeof item.corpo['ocorridoEm'] === 'string' ? new Date(item.corpo['ocorridoEm']) : null;
    melhor = { valor, em: quando && Number.isFinite(quando.getTime()) ? quando : null, doAparelho: true };
  }
  return melhor;
}

/**
 * A maquina com a ultima leitura da fila no lugar da do servidor: e o que as
 * verificacoes do lancamento ("medidor menor que a ultima leitura") e a
 * etiqueta leem. Sem item na fila, devolve a MESMA maquina. Pura.
 */
export function maquinaComAFila<T extends { readonly id: string; readonly ultimaLeitura: number | null; readonly ultimaLeituraEm: Date | null }>(
  maquina: T,
  fila: readonly ItemDaFila[],
): T & { readonly ultimaLeituraDoAparelho?: boolean } {
  const leitura = ultimaLeituraComAFila(maquina, fila);
  if (!leitura?.doAparelho) return maquina;
  return { ...maquina, ultimaLeitura: leitura.valor, ultimaLeituraEm: leitura.em, ultimaLeituraDoAparelho: true };
}

/** "hoje, 09:12" · "ontem, 17:04" · "14/10, 07:21". Pura. */
function quando(em: Date, agora: Date): string {
  const hora = em.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
  const dia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dias = Math.round((dia(agora) - dia(em)) / 86_400_000);
  if (dias === 0) return `hoje, ${hora}`;
  if (dias === 1) return `ontem, ${hora}`;
  return `${String(em.getDate()).padStart(2, '0')}/${String(em.getMonth() + 1).padStart(2, '0')}, ${hora}`;
}

/** O texto da etiqueta: "Última: 6.180,6 h · ontem, 23:00". Pura. */
export function textoDaUltimaLeitura(leitura: UltimaLeitura | null, unidade: 'h' | 'km', agora: Date = new Date()): string {
  if (leitura === null) return 'Sem leitura registrada';
  const valor = `Última: ${formatarAteDuasCasas(leitura.valor)} ${unidade}`;
  const momento = leitura.em ? quando(leitura.em, agora) : null;
  if (leitura.doAparelho) return `${valor} · lançada neste aparelho${momento ? `, ${momento}` : ''}`;
  return momento ? `${valor} · ${momento}` : valor;
}

/** A frase inteira, para o leitor de tela e o `title`. Pura. */
export function descricaoDaUltimaLeitura(leitura: UltimaLeitura | null, unidade: 'h' | 'km', agora: Date = new Date()): string {
  if (leitura === null) return 'Esta máquina ainda não tem leitura registrada.';
  const base = `A última leitura registrada é ${formatarAteDuasCasas(leitura.valor)} ${unidade}`;
  const momento = leitura.em ? `, de ${quando(leitura.em, agora)}` : '';
  const origem = leitura.doAparelho ? ', de um lançamento deste aparelho que ainda não foi enviado' : '';
  return `${base}${momento}${origem}. O número digitado não pode ser menor.`;
}
